import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, diasParaVencer } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useLang } from '../../contexts/LangContext';
import { supabase } from '../../lib/supabase';
import { carregarLembretesAutomaticos, LEMBRETES_AUTOMATICOS_PADRAO } from '../../lib/appConfig';
import { ativarNotificacoesPush, pushDisponivel, statusPermissaoPush } from '../../lib/pushNotifications';
import AlunaTabBar from '../../components/AlunaTabBar';
import RenovacaoBanner from '../../components/RenovacaoBanner';
import MetasMesCard from '../../components/MetasMesCard';
import { TIPOS } from '../../lib/questionarios';
import './HojeScreen.css';

/**
 * Tela Hoje — resumo do dia para o aluno no app Team Carol Macedo.
 * Tela piloto da v2 (Design System v3 · Soft Editorial Interface).
 *
 * Diferente do piloto original (que usava props com valores mock), este
 * componente busca os dados reais dos contexts existentes (AuthContext,
 * DataContext) e do Supabase, seguindo o mesmo padrão usado em
 * AlunaDashboard.js.
 *
 * Métricas do corpo (peso/gordura) vêm do último registro real de
 * evolucao_fisica — nada de dado inventado (steps, sono, frequência
 * cardíaca) sem equivalente no banco.
 */

const DIAS_SEMANA_LETRA = {
  pt: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
  en: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
};
const COPY = {
  pt: {
    bomDia: 'Bom dia',
    hoje: 'Hoje',
    feelingTitle: 'Como você está se sentindo?',
    feelingText: 'Toque em uma carinha para registrar seu ritmo de hoje.',
    registrado: 'registrado',
    alterar: 'Alterar resposta',
    recado: 'Recado da equipe',
    metas: 'Metas do mês',
    acompanhamento: 'Acompanhamento',
    plano: 'Plano alimentar',
    treino: 'Treino',
    saude: 'Saúde',
    evolucao: 'Minha evolução',
    agenda: 'Agenda',
    perfil: 'Perfil',
    aguardandoPlano: 'Aguardando plano',
    aguardandoTreino: 'Aguardando liberação',
    questionarios: 'Questionários e histórico',
    acompanharMedidas: 'Acompanhar medidas e progresso',
    marcarAvaliacao: 'Marcar avaliação',
    treinosSemana: 'Treinos da semana',
    de: 'de',
    ativarNotificacoes: 'Ativar notificações',
    notificacoesAtivas: 'Notificações ativas',
    notificacoesNegadas: 'Notificações bloqueadas no navegador',
    notificacoesErro: 'Não foi possível ativar agora.',
    onboardingEyebrow: 'Primeiros passos',
    onboardingTitle: 'Antes de começar…',
    onboardingText: 'Para que a equipe personalize seu acompanhamento, responda aos questionários iniciais. Leva apenas alguns minutos.',
    onboardingProgress: 'questionários respondidos',
    onboardingButton: 'Responder agora',
  },
  en: {
    bomDia: 'Good morning',
    hoje: 'Today',
    feelingTitle: 'How are you feeling?',
    feelingText: 'Tap a face to check in with your rhythm today.',
    registrado: 'checked in',
    alterar: 'Change answer',
    recado: 'Team note',
    metas: 'Monthly goals',
    acompanhamento: 'Follow-up',
    plano: 'Meal plan',
    treino: 'Workout',
    saude: 'Health',
    evolucao: 'My progress',
    agenda: 'Schedule',
    perfil: 'Profile',
    aguardandoPlano: 'Waiting for plan',
    aguardandoTreino: 'Waiting for release',
    questionarios: 'Questionnaires and history',
    acompanharMedidas: 'Track measurements and progress',
    marcarAvaliacao: 'Schedule assessment',
    treinosSemana: 'Weekly workouts',
    de: 'of',
    ativarNotificacoes: 'Enable notifications',
    notificacoesAtivas: 'Notifications enabled',
    notificacoesNegadas: 'Notifications blocked in browser',
    notificacoesErro: 'Could not enable them now.',
    onboardingEyebrow: 'First steps',
    onboardingTitle: 'Before you begin…',
    onboardingText: 'To help the team personalize your follow-up, please answer the 4 initial questionnaires. It only takes a few minutes.',
    onboardingProgress: 'questionnaires completed',
    onboardingButton: 'Answer now',
  },
};
const SENTIMENTOS = [
  { id: 'bem', label: { pt: 'Bem', en: 'Good' }, emoji: '🙂', detalhe: { pt: 'com energia', en: 'with energy' }, retorno: { pt: 'Perfeito. Mantém esse ritmo e segue no próximo passo simples de hoje.', en: 'Perfect. Keep this rhythm and take the next simple step today.' } },
  { id: 'cansaco', label: { pt: 'Cansaço', en: 'Tired' }, emoji: '😮‍💨', detalhe: { pt: 'preciso leveza', en: 'need ease' }, retorno: { pt: 'Tudo bem ir com calma. Hoje vale priorizar constância, hidratação e recuperação.', en: 'It is okay to take it easy. Today, prioritize consistency, hydration and recovery.' } },
  { id: 'ansiedade', label: { pt: 'Ansiedade', en: 'Anxious' }, emoji: '🤍', detalhe: { pt: 'quero foco', en: 'need focus' }, retorno: { pt: 'Respira. Escolha uma tarefa pequena agora e faça no seu ritmo.', en: 'Breathe. Choose one small task now and move at your pace.' } },
];

function lembreteAutomaticoDoDia(lang, lembretes = LEMBRETES_AUTOMATICOS_PADRAO) {
  const hoje = new Date();
  const lista = lembretes.length > 0 ? lembretes : LEMBRETES_AUTOMATICOS_PADRAO;
  const indice = hoje.getDate() % lista.length;
  return lista[indice][lang] || lista[indice].pt;
}

function recadoAindaRecente(recado) {
  if (!recado?.created_at) return false;
  const criado = new Date(recado.created_at);
  const diffDias = (Date.now() - criado.getTime()) / (1000 * 60 * 60 * 24);
  return diffDias <= 7;
}

function formatDateLabel(date, lang) {
  return date.toLocaleDateString(lang === 'pt' ? 'pt-BR' : 'en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

function startOfWeek(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

function Icon({ name }) {
  const common = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (name === 'run') return <svg {...common}><path d="M13 5.5a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6Z" /><path d="m7 21 3.4-7.5 3.3 2.7 2.3 4.8" /><path d="m5 10.6 4-1.8 3.2 2.6 4.8.4" /><path d="m10.4 13.5 1.7-4.8" /></svg>;
  if (name === 'food') return <svg {...common}><path d="M7 3v8" /><path d="M5 3v8" /><path d="M9 3v8" /><path d="M5 11h4l-.5 10h-3L5 11Z" /><path d="M16 3v18" /><path d="M16 3c2 1.2 3 3 3 5.2 0 2-1 3.6-3 4.8" /></svg>;
  if (name === 'clipboard') return <svg {...common}><path d="M9 4h6v3H9z" /><path d="M15 4h3a1 1 0 0 1 1 1v16H5V5a1 1 0 0 1 1-1h3" /><path d="M8 13h8" /><path d="M8 17h5" /></svg>;
  if (name === 'calendar') return <svg {...common}><rect x="4" y="5" width="16" height="15" rx="3" /><path d="M8 3v4" /><path d="M16 3v4" /><path d="M4 10h16" /></svg>;
  if (name === 'chart') return <svg {...common}><path d="M4 19V5" /><path d="M4 19h16" /><path d="m7 15 3-4 3 2 4-7" /><path d="M17 6h-4" /><path d="M17 6v4" /></svg>;
  return null;
}

// Avatar simples e somente leitura (foto se existir, senão inicial) —
// pra não puxar a lógica de upload da AvatarUpload só pra exibir.
function AvatarChip({ userId, nome, onClick }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!userId) return;
    const src = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/avatars/${userId}/avatar.jpg`;
    const img = new Image();
    img.onload = () => setUrl(src);
    img.onerror = () => setUrl(null);
    img.src = src;
  }, [userId]);
  const inicial = nome?.charAt(0).toUpperCase() || 'A';
  return (
    <button className="hoje-avatar" onClick={onClick}>
      {url ? <img src={url} alt={nome} /> : <span>{inicial}</span>}
    </button>
  );
}

export default function HojeScreen() {
  const { user } = useAuth();
  const { treinos, dietas, checkins } = useData();
  const { lang, setLang } = useLang();
  const c = COPY[lang] || COPY.pt;
  const navigate = useNavigate();
  const [sentimentoHoje, setSentimentoHoje] = useState(null);
  const [ultimoRecado, setUltimoRecado] = useState(null);
  const [lembretesAuto, setLembretesAuto] = useState(LEMBRETES_AUTOMATICOS_PADRAO);
  const [pushStatus, setPushStatus] = useState(() => statusPermissaoPush());
  const [pushMsg, setPushMsg] = useState('');
  const [ativandoPush, setAtivandoPush] = useState(false);
  const [questionariosRespondidos, setQuestionariosRespondidos] = useState(null);

  // Peso/gordura vêm de evolucao_fisica (fonte real de medidas) — antes
  // este componente lia perfis.peso_atual, coluna que não existe mais.
  const [registrosEvolucao, setRegistrosEvolucao] = useState([]);

  useEffect(() => {
    if (!user?.id) return;
    supabase.from('evolucao_fisica').select('peso, gordura, data')
      .eq('aluna_id', user.id).order('data', { ascending: false }).limit(2)
      .then(({ data }) => setRegistrosEvolucao(data || []));
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    supabase.from('questionario_respostas').select('tipo').eq('aluna_id', user.id)
      .then(({ data }) => {
        const tiposUnicos = new Set((data || []).map(resposta => resposta.tipo));
        setQuestionariosRespondidos(TIPOS.filter(tipo => tiposUnicos.has(tipo)).length);
      });
  }, [user]);

  useEffect(() => {
    carregarLembretesAutomaticos().then(setLembretesAuto);
  }, []);

  async function ativarPush() {
    setAtivandoPush(true);
    setPushMsg('');
    const result = await ativarNotificacoesPush();
    setPushStatus(statusPermissaoPush());
    setPushMsg(result.ok ? c.notificacoesAtivas : (result.error || c.notificacoesErro));
    setAtivandoPush(false);
  }

  useEffect(() => {
    if (!user?.id) return;
    supabase.from('mensagens').select('*')
      .eq('aluna_id', user.id)
      .eq('remetente', 'admin')
      .order('created_at', { ascending: false })
      .limit(1)
      .then(({ data }) => setUltimoRecado(data?.[0] || null));
  }, [user]);

  const [ultimoRegistro, registroAnterior] = registrosEvolucao;
  const pesoAtual = ultimoRegistro?.peso != null ? parseFloat(ultimoRegistro.peso) : null;
  const pesoVariacao = (pesoAtual != null && registroAnterior?.peso != null) ? pesoAtual - parseFloat(registroAnterior.peso) : null;
  const gorduraAtual = ultimoRegistro?.gordura != null ? parseFloat(ultimoRegistro.gordura) : null;

  const meusTreinos = (treinos || [])
    .filter(t => t.alunaId === user?.id)
    .sort((a, b) => (a.ordem || a.semana) - (b.ordem || b.semana));
  const treinosPrincipais = meusTreinos.filter(t => !/bloco|complement|core/i.test(`${t.titulo || ''} ${t.descricao || ''}`));
  const meusCheckins = (checkins || []).filter(c => c.alunaId === user?.id);
  const minhasDietas = (dietas || []).filter(d => d.alunaId === user?.id);

  const inicioSemana = startOfWeek(new Date());
  const checkinsEstaSemana = meusCheckins.filter(c => new Date(c.criadoEm) >= inicioSemana);
  const temTreinos = meusTreinos.length > 0;
  const metaSemana = treinosPrincipais.length || meusTreinos.length;
  const percentSemana = temTreinos ? Math.min(Math.round((checkinsEstaSemana.length / metaSemana) * 100), 100) : 0;

  const diasDaSemana = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(inicioSemana);
    d.setDate(inicioSemana.getDate() + i);
    return {
      letra: (DIAS_SEMANA_LETRA[lang] || DIAS_SEMANA_LETRA.pt)[i],
      feito: checkinsEstaSemana.some(c => new Date(c.criadoEm).toDateString() === d.toDateString()),
      hoje: d.toDateString() === new Date().toDateString(),
    };
  });

  const ultimoCheckin = [...meusCheckins].sort((a, b) => new Date(b.criadoEm) - new Date(a.criadoEm))[0];
  const idxUltimo = treinosPrincipais.findIndex(t => t.id === ultimoCheckin?.treinoId);
  const proximoTreino = treinosPrincipais.length > 0 ? treinosPrincipais[(idxUltimo + 1) % treinosPrincipais.length] : null;

  // Próxima refeição de verdade: compara o horário de cada refeição com a
  // hora atual e pega a primeira que ainda não passou; se todas já
  // passaram hoje, volta pra mais cedo (primeira refeição do dia seguinte).
  const dietaAtual = minhasDietas[minhasDietas.length - 1];
  function calcularProximaRefeicao(refeicoes) {
    if (!refeicoes || refeicoes.length === 0) return null;
    const agora = new Date();
    const minutosAgora = agora.getHours() * 60 + agora.getMinutes();
    const comHorario = refeicoes
      .map(r => {
        if (!r.horario) return null;
        const [h, m] = r.horario.split(':').map(Number);
        return { ...r, minutos: h * 60 + (m || 0) };
      })
      .filter(Boolean);
    if (comHorario.length === 0) return refeicoes[0];
    const proxima = comHorario.filter(r => r.minutos >= minutosAgora).sort((a, b) => a.minutos - b.minutos)[0];
    return proxima || comHorario.sort((a, b) => a.minutos - b.minutos)[0];
  }
  const proximaRefeicao = calcularProximaRefeicao(dietaAtual?.refeicoes);

  const firstName = user?.name?.split(' ')[0] || '';
  const diasVencer = diasParaVencer(user?.inicioPlano, user?.plano);
  const sentimentos = SENTIMENTOS;
  const recadoManual = recadoAindaRecente(ultimoRecado) ? ultimoRecado : null;
  const recadoTela = recadoManual?.texto || lembreteAutomaticoDoDia(lang, lembretesAuto);
  const modulos = [
    {
      titulo: c.plano,
      desc: user?.acessoDieta === false
        ? (lang === 'pt' ? 'Não incluído no pacote atual' : 'Not included in your current package')
        : proximaRefeicao ? `${proximaRefeicao.nome}${proximaRefeicao.horario ? ` às ${proximaRefeicao.horario}` : ''}` : c.aguardandoPlano,
      tom: user?.acessoDieta === false ? 'neutro' : 'verde',
      path: '/aluna/nutricao',
      icon: 'food',
    },
    {
      titulo: c.treino,
      desc: proximoTreino ? proximoTreino.titulo : c.aguardandoTreino,
      tom: 'terra',
      path: '/aluna/treino',
      icon: 'run',
    },
    {
      titulo: c.saude,
      desc: pesoAtual != null ? `${lang === 'pt' ? 'Peso atual' : 'Current weight'} ${pesoAtual.toFixed(1)} kg` : c.questionarios,
      tom: 'azul',
      path: '/aluna/saude',
      icon: 'clipboard',
    },
    {
      titulo: c.evolucao,
      desc: gorduraAtual != null ? `${lang === 'pt' ? 'Gordura atual' : 'Current body fat'} ${gorduraAtual.toFixed(1)}%` : c.acompanharMedidas,
      tom: 'lilas',
      path: '/aluna/evolucao',
      icon: 'chart',
    },
    {
      titulo: c.agenda,
      desc: c.marcarAvaliacao,
      tom: 'coral',
      path: '/aluna/agendar',
      icon: 'calendar',
    },
  ];

  return (
    <div className="hoje-screen">
      <RenovacaoBanner dias={diasVencer} lang={lang} />

      <div className="hoje-phone">
        <div className="hoje-topbar">
          <img src="/logo.svg" alt="Team Carol Macedo" />
          <div className="hoje-top-actions">
            <div className="hoje-lang-toggle" aria-label="Idioma do app">
              <button className={lang === 'pt' ? 'is-active' : ''} onClick={() => setLang('pt')}>PT</button>
              <button className={lang === 'en' ? 'is-active' : ''} onClick={() => setLang('en')}>EN</button>
            </div>
            <button className="hoje-profile-link" onClick={() => navigate('/aluna/perfil')}>{c.perfil}</button>
          </div>
        </div>

        <header className="hoje-greeting">
          <AvatarChip userId={user?.id} nome={user?.name} onClick={() => navigate('/aluna/perfil')} />
          <div>
            <p>{formatDateLabel(new Date(), lang)}</p>
            <h1>{c.bomDia}, {firstName}</h1>
          </div>
        </header>

        {proximoTreino && (
          <section className="hoje-onboarding-card" style={{ background:'linear-gradient(145deg,#251c19,#5b402e)', color:'#fff' }}>
            <div className="hoje-onboarding-icon"><Icon name="run" /></div>
            <div className="hoje-onboarding-copy">
              <span style={{ color:'#e8d5c7' }}>{lang==='pt'?'Seu treino de hoje':'Your workout today'}</span>
              <h2 style={{ color:'#fff' }}>{proximoTreino.titulo}</h2>
              <p style={{ color:'#eadfd4' }}>{proximoTreino.descricao} · {Math.max(20, Math.min(75, Math.round(((proximoTreino.exercicios?.length || 0) * 3.5) / 5) * 5))} min</p>
              <button type="button" onClick={() => navigate('/aluna/treino')} style={{ background:'#fff', color:'#251c19' }}>
                {lang==='pt'?'Ver e começar treino':'View and start workout'} <span aria-hidden="true">→</span>
              </button>
            </div>
          </section>
        )}

        {questionariosRespondidos !== null && questionariosRespondidos < TIPOS.length && (
          <section className="hoje-onboarding-card">
            <div className="hoje-onboarding-icon"><Icon name="clipboard" /></div>
            <div className="hoje-onboarding-copy">
              <span>{c.onboardingEyebrow}</span>
              <h2>{c.onboardingTitle}</h2>
              <p>{c.onboardingText}</p>
              <div className="hoje-onboarding-status">
                <div>
                  <i style={{ width: `${Math.round((questionariosRespondidos / TIPOS.length) * 100)}%` }} />
                </div>
                <small>{questionariosRespondidos} {c.de} {TIPOS.length} {c.onboardingProgress}</small>
              </div>
              <button type="button" onClick={() => navigate('/aluna/questionarios')}>
                {c.onboardingButton}
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </section>
        )}

        <section className="hoje-calendar" aria-label="Semana">
          {diasDaSemana.slice(0, 6).map((dia, index) => (
            <div key={`${dia.letra}-${index}`} className={`${dia.hoje ? 'is-active' : ''}${dia.feito ? ' is-done' : ''}`}>
              <span>{dia.letra}</span>
              <strong>{new Date(inicioSemana.getFullYear(), inicioSemana.getMonth(), inicioSemana.getDate() + index).getDate()}</strong>
              {dia.feito && <i />}
            </div>
          ))}
        </section>

        <section className="hoje-focus-card">
          <span>{c.hoje}</span>
          {!sentimentoHoje ? (
            <>
              <h2>{c.feelingTitle}</h2>
              <p>{c.feelingText}</p>
              <div className="hoje-feeling-grid">
                {sentimentos.map(opcao => (
                  <button
                    key={opcao.id}
                    type="button"
                    className="hoje-feeling-option"
                    onClick={() => setSentimentoHoje(opcao.id)}
                  >
                    <span className="hoje-feeling-emoji">{opcao.emoji}</span>
                    <span className="hoje-feeling-label">{opcao.label[lang] || opcao.label.pt}</span>
                    <span className="hoje-feeling-detail">{opcao.detalhe[lang] || opcao.detalhe.pt}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (() => {
            const escolhido = sentimentos.find(opcao => opcao.id === sentimentoHoje);
            return (
              <div className="hoje-feeling-result">
                <div className="hoje-feeling-result-emoji">{escolhido?.emoji}</div>
                <div>
                  <h2>{escolhido?.label[lang] || escolhido?.label.pt} {c.registrado}</h2>
                  <p>{escolhido?.retorno[lang] || escolhido?.retorno.pt}</p>
                  <button type="button" onClick={() => setSentimentoHoje(null)}>{c.alterar}</button>
                </div>
              </div>
            );
          })()}
        </section>

        {recadoTela && (
          <section className={`hoje-team-message ${recadoManual ? 'is-admin-note' : 'is-auto-note'}`}>
            <div className="hoje-team-avatar">TC</div>
            <div className="hoje-team-content">
              <div className="hoje-team-meta">
                <span>Team Carol Macedo</span>
                <strong>{recadoManual ? c.recado : (lang === 'pt' ? 'Lembrete do dia' : 'Daily reminder')}</strong>
              </div>
              <p>{recadoTela}</p>
            </div>
          </section>
        )}

        {pushDisponivel() && pushStatus !== 'granted' && (
          <section className="hoje-push-card">
            <div>
              <strong>{c.ativarNotificacoes}</strong>
              <span>{lang === 'pt' ? 'Receba recados da equipe e lembretes do dia.' : 'Receive team notes and daily reminders.'}</span>
              {pushMsg && <small>{pushStatus === 'denied' ? c.notificacoesNegadas : pushMsg}</small>}
            </div>
            <button type="button" onClick={ativarPush} disabled={ativandoPush || pushStatus === 'denied'}>
              {ativandoPush ? '...' : (lang === 'pt' ? 'Ativar' : 'Enable')}
            </button>
          </section>
        )}

        <MetasMesCard alunaId={user?.id} lang={lang} />

        <section className="hoje-section">
          <div className="hoje-section-head">
            <h2>{c.acompanhamento}</h2>
          </div>
          <div className="hoje-module-stack">
            {modulos.map((modulo, index) => (
              <button key={modulo.titulo} className={`hoje-module hoje-${modulo.tom}`} onClick={() => navigate(modulo.path)}>
                <span><Icon name={modulo.icon} /></span>
                <div>
                  <strong>{modulo.titulo}</strong>
                  <small>{modulo.desc}</small>
                </div>
              </button>
            ))}
          </div>
        </section>

        {temTreinos && (
          <section className="hoje-section hoje-week-compact">
            <div className="hoje-section-head">
              <h2>{c.treinosSemana}</h2>
              <span>{checkinsEstaSemana.length} {c.de} {metaSemana}</span>
            </div>
            <div className="hoje-week-days">
              {diasDaSemana.map((d, i) => (
                <div key={i} className={`${d.feito ? 'is-done' : ''}${d.hoje ? ' is-today' : ''}`}>
                  <span />
                  <strong>{d.letra}</strong>
                </div>
              ))}
            </div>
          </section>
        )}

        <AlunaTabBar current="hoje" />
      </div>
    </div>
  );
}
